import {
  createAdminRemoteTakeover,
  getCurrentAdminRemoteTakeover,
  getAdminRemoteTakeover,
  cancelAdminRemoteTakeover,
  launchAdminRemoteTakeover,
} from '../../../api/generated/scope-filtered/scope-filtered'
import {
  approveAdminRemoteHost,
  setAdminRemoteHostOwner,
  listAdminRemoteSellerOrgs,
  closeAdminRemoteSession,
  closeOwnedAdminRemoteSession,
  createAdminRemoteLaunch,
  getAdminRemoteSession,
  listAdminRemoteHosts,
  reconcileAdminRemoteAccess,
  listAdminRemoteBoardStores,
  listAdminRemoteBoardStoreLabels,
  assignAdminRemoteHostStoreLabel,
  removeAdminRemoteHostStoreLabel,
  unlinkAdminRemoteHostStore,
  listAdminStoreProductGroups,
  assignAdminStoreProductGroup,
  removeAdminStoreProductGroup,
} from '../../../api/generated/remote-access/remote-access'
import type {
  RemoteTakeoverRead,
  RemoteHostRead,
  RemoteHostOwnerWrite,
  RemoteSellerOrgRead,
  RemoteInventoryIssueRead,
  RemoteLaunchIntent,
  RemoteSessionRead,
  RemoteSessionStatus,
  RemoteBoardStoresRead,
  RemoteBoardStoreLabelsRead,
  RemoteStoreLabelRead,
  StoreProductGroupsRead,
  StoreProductGroupRead,
} from '../../../api/generated/model'

export type RemoteAccessSafeError = {
  status: number | null
  code:
    | 'remote_access_not_available'
    | 'remote_access_forbidden'
    | 'remote_access_reauthentication_required'
    | 'remote_access_conflict'
    | 'remote_access_inventory_not_ready'
    // Store links. Listed here so a refusal keeps its own name instead of
    // collapsing into the generic "unavailable" the UI cannot explain.
    | 'remote_access_store_linked_elsewhere'
    | 'remote_access_store_already_linked'
    | 'remote_access_store_not_found'
    | 'remote_access_store_link_not_found'
    | 'remote_access_store_label_already_assigned'
    | 'remote_access_store_label_assigned_elsewhere'
    | 'remote_access_store_label_not_found'
    | 'remote_access_unavailable'
  correlationId: string | null
}

const SAFE_ERROR_CODES = new Set<RemoteAccessSafeError['code']>([
  'remote_access_not_available',
  'remote_access_forbidden',
  'remote_access_reauthentication_required',
  'remote_access_conflict',
  'remote_access_inventory_not_ready',
  'remote_access_store_linked_elsewhere',
  'remote_access_store_already_linked',
  'remote_access_store_not_found',
  'remote_access_store_link_not_found',
  'remote_access_store_label_already_assigned',
  'remote_access_store_label_assigned_elsewhere',
  'remote_access_store_label_not_found',
  'remote_access_unavailable',
])

const REMOTE_SESSION_STATUSES = new Set<RemoteSessionStatus>([
  'reserved',
  'connecting',
  'active',
  'status_unknown',
  'closing',
  'closed',
  'failed',
])

export type LaunchOutcome =
  | { kind: 'created'; intent: RemoteLaunchIntent }
  | {
      kind: 'replayed'
      sessionId: string
      status: RemoteSessionStatus
      correlationId: string
    }

type OrvalFailure = {
  status?: unknown
  problem?: unknown
}

type ReplayOutcome = Extract<LaunchOutcome, { kind: 'replayed' }>

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isRemoteAccessSafeError(error: unknown): error is RemoteAccessSafeError {
  return record(error)
    && (typeof error.status === 'number' || error.status === null)
    && typeof error.code === 'string'
    && SAFE_ERROR_CODES.has(error.code as RemoteAccessSafeError['code'])
    && (typeof error.correlationId === 'string' || error.correlationId === null)
}

function safeErrorFromResponse(response: { status: number; data: unknown }): RemoteAccessSafeError {
  return toRemoteAccessSafeError({
    status: response.status,
    problem: response.data,
  })
}

function replayOutcome(detail: unknown): ReplayOutcome | null {
  if (
    !record(detail) ||
    detail.code !== 'remote_access_idempotency_replayed' ||
    typeof detail.session_id !== 'string' ||
    typeof detail.status !== 'string' ||
    !REMOTE_SESSION_STATUSES.has(detail.status as RemoteSessionStatus) ||
    typeof detail.correlation_id !== 'string'
  ) {
    return null
  }
  return {
    kind: 'replayed',
    sessionId: detail.session_id,
    status: detail.status as RemoteSessionStatus,
    correlationId: detail.correlation_id,
  }
}

export function toRemoteAccessSafeError(error: unknown): RemoteAccessSafeError {
  if (isRemoteAccessSafeError(error)) return error
  const failure = record(error) ? (error as OrvalFailure) : {}
  const status = typeof failure.status === 'number' ? failure.status : null
  const problem = record(failure.problem) ? failure.problem : null
  const detail = problem && record(problem.detail) ? problem.detail : null
  const rawCode = detail && typeof detail.code === 'string' ? detail.code : null
  const correlationId =
    detail && typeof detail.correlation_id === 'string'
      ? detail.correlation_id
      : problem && typeof problem.correlation_id === 'string'
        ? problem.correlation_id
        : null

  const code = status === 401 && rawCode === 'auth_session_reauthentication_required'
    ? 'remote_access_reauthentication_required'
    : status === 404 && rawCode === 'remote_access_not_available'
      ? 'remote_access_not_available'
      : status === 403
        ? 'remote_access_forbidden'
        : status === 409
          ? rawCode === 'remote_access_inventory_not_ready'
            ? 'remote_access_inventory_not_ready'
            : 'remote_access_conflict'
          : 'remote_access_unavailable'
  return { status, code, correlationId }
}

export type RemoteHostInventory = {
  hosts: RemoteHostRead[]
  inventoryIssues: RemoteInventoryIssueRead[]
  inventoryObservedAt: string | null
}

export async function listRemoteHostInventory(): Promise<RemoteHostInventory> {
  let response: Awaited<ReturnType<typeof listAdminRemoteHosts>>
  try {
    response = await listAdminRemoteHosts()
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return {
    hosts: response.data.items,
    inventoryIssues: response.data.inventory_issues ?? [],
    inventoryObservedAt: response.data.inventory_observed_at ?? null,
  }
}

export async function listAssignedRemoteHosts(): Promise<RemoteHostRead[]> {
  return (await listRemoteHostInventory()).hosts
}

export async function requestRemoteLaunch(
  hostId: string,
  idempotencyKey: string,
): Promise<LaunchOutcome> {
  return requestLaunchVia(() => createAdminRemoteLaunch(hostId, {
    headers: { 'Idempotency-Key': idempotencyKey },
  }))
}

type LaunchResponse = { status: number; data: unknown }

/** One launch call, Admin's or Seller's: same intent, same replay contract. */
export async function requestLaunchVia(
  call: () => Promise<LaunchResponse>,
): Promise<LaunchOutcome> {
  let response: LaunchResponse
  try {
    response = await call()
  } catch (error) {
    const failure = record(error) ? error : null
    const problem = failure && record(failure.problem) ? failure.problem : null
    const detail = problem && record(problem.detail) ? problem.detail : null
    const replay = failure?.status === 409 ? replayOutcome(detail) : null
    if (replay) {
      return replay
    }
    throw toRemoteAccessSafeError(error)
  }
  if (response.status === 409) {
    const data = record(response.data) ? response.data : null
    const replay = replayOutcome(data?.detail)
    if (replay) {
      return replay
    }
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return { kind: 'created', intent: response.data as RemoteLaunchIntent }
}

export async function readRemoteSession(sessionId: string): Promise<RemoteSessionRead> {
  let response: Awaited<ReturnType<typeof getAdminRemoteSession>>
  try {
    response = await getAdminRemoteSession(sessionId)
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function closeKnownRemoteSession(
  sessionId: string,
  reason: string,
): Promise<RemoteSessionRead> {
  let response: Awaited<ReturnType<typeof closeAdminRemoteSession>>
  try {
    response = await closeAdminRemoteSession(sessionId, { reason })
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function closeOwnedRemoteSession(
  sessionId: string,
  reason: string,
): Promise<RemoteSessionRead> {
  let response: Awaited<ReturnType<typeof closeOwnedAdminRemoteSession>>
  try {
    response = await closeOwnedAdminRemoteSession(sessionId, { reason })
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function reconcileRemoteAccess(reason: string) {
  let response: Awaited<ReturnType<typeof reconcileAdminRemoteAccess>>
  try {
    response = await reconcileAdminRemoteAccess({ reason })
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function approveRemoteHost(hostId: string): Promise<RemoteHostRead> {
  let response: Awaited<ReturnType<typeof approveAdminRemoteHost>>
  try {
    response = await approveAdminRemoteHost(hostId)
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function setRemoteHostOwner(
  hostId: string, owner: RemoteHostOwnerWrite,
): Promise<RemoteHostRead> {
  let response: Awaited<ReturnType<typeof setAdminRemoteHostOwner>>
  try {
    response = await setAdminRemoteHostOwner(hostId, owner)
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function listRemoteSellerOrgs(signal?: AbortSignal): Promise<RemoteSellerOrgRead[]> {
  let response: Awaited<ReturnType<typeof listAdminRemoteSellerOrgs>>
  try {
    response = await listAdminRemoteSellerOrgs({ signal })
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data.items
}

// These wrappers never retry mutations or replay a one-use takeover ticket.
export async function createRemoteTakeover(
  hostId: string, reason: string, key: string, signal?: AbortSignal,
): Promise<RemoteTakeoverRead> {
  try {
    const response = await createAdminRemoteTakeover(hostId, { reason: reason.trim() }, { signal, headers: { 'Idempotency-Key': key } })
    if (response.status !== 200) throw safeErrorFromResponse(response)
    return response.data
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
}

export async function readRemoteTakeover(requestId: string, signal?: AbortSignal): Promise<RemoteTakeoverRead> {
  try {
    const response = await getAdminRemoteTakeover(requestId, { signal })
    if (response.status !== 200) throw safeErrorFromResponse(response)
    return response.data
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
}

export async function readCurrentRemoteTakeover(signal?: AbortSignal): Promise<RemoteTakeoverRead | null> {
  try {
    const response = await getCurrentAdminRemoteTakeover({ signal })
    if (response.status !== 200) throw safeErrorFromResponse(response)
    return response.data
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
}

export async function cancelRemoteTakeover(requestId: string, signal?: AbortSignal): Promise<RemoteTakeoverRead> {
  try {
    const response = await cancelAdminRemoteTakeover(requestId, {}, { signal })
    if (response.status !== 200) throw safeErrorFromResponse(response)
    return response.data
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
}

export async function launchRemoteTakeover(
  requestId: string, key: string, signal?: AbortSignal,
): Promise<RemoteLaunchIntent> {
  try {
    const response = await launchAdminRemoteTakeover(requestId, {}, { signal, headers: { 'Idempotency-Key': key } })
    if (response.status !== 200) throw safeErrorFromResponse(response)
    return response.data
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
}

export async function listRemoteBoardStores(): Promise<RemoteBoardStoresRead> {
  let response: Awaited<ReturnType<typeof listAdminRemoteBoardStores>>
  try {
    response = await listAdminRemoteBoardStores()
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function listRemoteBoardStoreLabels(): Promise<RemoteBoardStoreLabelsRead> {
  let response: Awaited<ReturnType<typeof listAdminRemoteBoardStoreLabels>>
  try {
    response = await listAdminRemoteBoardStoreLabels()
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) throw safeErrorFromResponse(response)
  return response.data
}

export async function assignRemoteHostStoreLabel(
  hostId: string,
  displayName: string,
): Promise<RemoteStoreLabelRead> {
  let response: Awaited<ReturnType<typeof assignAdminRemoteHostStoreLabel>>
  try {
    response = await assignAdminRemoteHostStoreLabel(hostId, { display_name: displayName })
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 201) throw safeErrorFromResponse(response)
  return response.data
}

export async function removeRemoteHostStoreLabel(hostId: string, labelId: string): Promise<void> {
  let response: Awaited<ReturnType<typeof removeAdminRemoteHostStoreLabel>>
  try {
    response = await removeAdminRemoteHostStoreLabel(hostId, labelId)
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 204) throw safeErrorFromResponse(response)
}

export async function unlinkRemoteHostStore(hostId: string, linkId: string): Promise<void> {
  let response: Awaited<ReturnType<typeof unlinkAdminRemoteHostStore>>
  try {
    response = await unlinkAdminRemoteHostStore(hostId, linkId)
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 204) {
    throw safeErrorFromResponse(response)
  }
}

// Store mix (Servers board — Store mix tab). Independent of any machine: every
// internal store with its free-form product-group tags, in one read.
export async function listStoreProductGroups(): Promise<StoreProductGroupsRead> {
  let response: Awaited<ReturnType<typeof listAdminStoreProductGroups>>
  try {
    response = await listAdminStoreProductGroups()
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 200) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function assignStoreProductGroup(
  storeIntegrationId: string,
  displayName: string,
): Promise<StoreProductGroupRead> {
  let response: Awaited<ReturnType<typeof assignAdminStoreProductGroup>>
  try {
    response = await assignAdminStoreProductGroup({
      store_integration_id: storeIntegrationId,
      display_name: displayName,
    })
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 201) {
    throw safeErrorFromResponse(response)
  }
  return response.data
}

export async function removeStoreProductGroup(groupId: string): Promise<void> {
  let response: Awaited<ReturnType<typeof removeAdminStoreProductGroup>>
  try {
    response = await removeAdminStoreProductGroup(groupId)
  } catch (error) {
    throw toRemoteAccessSafeError(error)
  }
  if (response.status !== 204) {
    throw safeErrorFromResponse(response)
  }
}
